import {
  isTraversalLoadedState,
  ReferenceDirection,
  type LoadedState,
  type QueryBuilderQuery,
} from '@ardoq/api-types';
import { trackEventV2 } from 'tracking/tracking';
import type { TrackingEvent } from 'tracking/trackPayload';
import { countTraversalHops } from 'loadedState/countTraversalHops';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import type { TraversalErrorReason } from 'loadedState/traversalTypes';
import type { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import type {
  PathCollapsingRuleInternal,
  TraversalBuilderViewState,
} from 'viewpointBuilder/types';
import { getAdvancedSearchRuleCount } from './selectContext/toSelectContextFilters';
import {
  getValidAdvancedSearchQuery,
  isAdvancedSearchMode,
} from './selectContext/startQueryConversion';

type ContextSelectionMode = 'create' | 'edit';

const toSearchMode = (
  searchState: ComponentSearchViewState
): 'simple' | 'advanced' =>
  isAdvancedSearchMode(searchState) ? 'advanced' : 'simple';

const toRuleCount = (searchState: ComponentSearchViewState): number => {
  const query = getValidAdvancedSearchQuery(searchState.advancedSearchState);
  return query ? getAdvancedSearchRuleCount(query) : 0;
};

const opened = (): void => trackEventV2({ event: 'Datasets panel0 Opened' });

const closed = (): void => trackEventV2({ event: 'Datasets panel0 Closed' });

const isDatasetsPanelTab = (tab: string): tab is 'datasets' | 'components' =>
  tab === 'datasets' || tab === 'components';

const switchedTab = (tab: string): void => {
  if (!isDatasetsPanelTab(tab)) return;
  trackEventV2({ event: 'Datasets panel0 Switched tab', payload: { tab } });
};

const toOpenedContextSelectionEvent = (
  mode: ContextSelectionMode
): TrackingEvent => ({
  event: 'Datasets panel0 Opened context selection',
  payload: { mode },
});

const savedAdvancedRules = (query: QueryBuilderQuery): void =>
  trackEventV2({
    event: 'Datasets panel0 Saved advanced rules',
    payload: { ruleCount: getAdvancedSearchRuleCount(query) },
  });

const confirmedContext = (
  searchState: ComponentSearchViewState,
  mode: ContextSelectionMode
): void =>
  trackEventV2({
    event: 'Datasets panel0 Confirmed context',
    payload: {
      mode,
      searchMode: toSearchMode(searchState),
      selection: searchState.allComponentsSelected ? 'select_all' : 'picked',
      componentCount: searchState.selectedComponentsCount,
      componentTypeCount: searchState.selectedStartComponentTypeNames.length,
      usedComponentTypeFilter: Boolean(searchState.componentType),
      usedNameSearch: Boolean(searchState.componentName),
      ruleCount: toRuleCount(searchState),
    },
  });

type AbandonedContextSelectionArgs = {
  searchState: ComponentSearchViewState;
  mode: ContextSelectionMode;
  via: 'back' | 'close';
};

const abandonedContextSelection = ({
  searchState,
  mode,
  via,
}: AbandonedContextSelectionArgs): void =>
  trackEventV2({
    event: 'Datasets panel0 Abandoned context selection',
    payload: {
      mode,
      via,
      searchMode: toSearchMode(searchState),
      hadSelection:
        searchState.selectedComponentIds.length > 0 ||
        searchState.allComponentsSelected,
    },
  });

const failedToBuildNewDataset = (reason: TraversalErrorReason): void =>
  trackEventV2({
    event: 'Datasets panel0 Failed to create dataset',
    payload: { stage: 'build_traversal', errorKind: reason },
  });

const failedToRegisterNewDataset = (): void =>
  trackEventV2({
    event: 'Datasets panel0 Failed to create dataset',
    payload: { stage: 'register_dataset', errorKind: 'exception' },
  });

const failedToCommitTraversal = (reason: TraversalErrorReason): void =>
  trackEventV2({
    event: 'Datasets panel0 Failed to commit traversal',
    payload: { errorKind: reason },
  });

type ToggledTripleOptionArgs = {
  tripleOptions: TraversalBuilderViewState['filteredTripleOptions'];
  tripleId: string;
  direction: ReferenceDirection;
  isSelected: boolean;
};

const toggledTripleOption = ({
  tripleOptions,
  tripleId,
  direction,
  isSelected,
}: ToggledTripleOptionArgs): void => {
  const allOptions = [...tripleOptions.outgoing, ...tripleOptions.incoming];
  trackEventV2({
    event: 'Datasets panel0 Toggled triple option',
    payload: {
      enabled: isSelected,
      direction:
        direction === ReferenceDirection.INCOMING ? 'incoming' : 'outgoing',
      optionCountAfter:
        allOptions.filter(option => option.isSelected).length +
        (isSelected ? 1 : -1),
    },
  });
  if (!isSelected) return;
  trackEventV2({
    event: 'Datasets panel0 Added hop',
    payload: {
      resultComponentCount:
        allOptions.find(option => option.tripleId === tripleId)
          ?.instanceCounts ?? null,
    },
  });
};

const clickedShowAll = (editTraversalsState: TraversalBuilderViewState): void =>
  trackEventV2({
    event: 'Datasets panel0 Clicked show all',
    payload: {
      showingAll:
        editTraversalsState.triplesSortAndFiltersState
          .showOnlyOptionsWithInstanceCounts,
    },
  });

const removedHop = (): void =>
  trackEventV2({ event: 'Datasets panel0 Removed hop' });

const changedWildcard = (to: 'show_all_direct' | 'start_from_scratch'): void =>
  trackEventV2({
    event: 'Datasets panel0 Changed wildcard',
    payload: { to },
  });

type ToggledCollapsedPathArgs = {
  rules: PathCollapsingRuleInternal[];
  referenceTypeId: string;
  isActive: boolean;
};

const toggledCollapsedPath = ({
  rules,
  referenceTypeId,
  isActive,
}: ToggledCollapsedPathArgs): void => {
  const toggledRule = rules.find(
    rule => rule.referenceTypeId === referenceTypeId
  );
  if (!toggledRule) return;
  trackEventV2({
    event: 'Datasets panel0 Toggled collapsed path',
    payload: {
      active: isActive,
      pathLength: toggledRule.path.length,
      isRecursive: Boolean(toggledRule.collapseRecursively),
      activeRuleCountAfter: rules.filter(rule =>
        rule === toggledRule ? isActive : rule.isActive
      ).length,
      totalRuleCount: rules.length,
    },
  });
};

const editedContext = (
  componentCountBefore: number,
  componentCountAfter: number
): void =>
  trackEventV2({
    event: 'Datasets panel0 Edited context',
    payload: { componentCountBefore, componentCountAfter },
  });

const toClickedRemoveDatasetEvent = (
  loadedState: LoadedState
): TrackingEvent => ({
  event: 'Datasets panel0 Clicked remove dataset',
  payload: {
    hopCount: isTraversalLoadedState(loadedState)
      ? countTraversalHops(loadedState.data.paths)
      : 0,
    componentCount:
      loadedStateOperations.getScopeComponentIds(loadedState).length,
  },
});

export const improvedDatasetsTracking = {
  opened,
  closed,
  switchedTab,
  toOpenedContextSelectionEvent,
  savedAdvancedRules,
  confirmedContext,
  abandonedContextSelection,
  failedToBuildNewDataset,
  failedToRegisterNewDataset,
  failedToCommitTraversal,
  toggledTripleOption,
  clickedShowAll,
  removedHop,
  changedWildcard,
  toggledCollapsedPath,
  editedContext,
  toClickedRemoveDatasetEvent,
};
